import { useMemo, useState, useRef } from "react"
import { useLocation } from "react-router-dom"

import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext"
import { SectionHeading, TableCustom, FooterButtonVolver } from "../../components/common"
import TipoMultaModal from "./components/TipoMultaModal"
import { ServerRequest } from "../../utils/apiweb"
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { useLista, useManagedContext } from "../../components/hooks"
import TipoMultaFilters from "./components/TipoMultaFilters"
import { PERMISSIONS } from "../../consts/permissions"
import { usePermissions } from "../../components/hooks/usePermissions"
import { useSearchParams } from "react-router-dom"
export const UNIDAD_VALORES = {
    PORCENTAJE: 490,
    IMPORTE: 491,
    UF: 492,
}

const TipoMultasView = () => {
    const { setIsLoading, showMessageModal } = useManagedContext()
    const location = useLocation()
    const previousFilters = location.state?.filters

    const [multas, setMultas] = useState([])
    const [editState, setEditState] = useState({ mode: null, item: null })
    const [filters, setFilters] = useState({})
    const [tableVisible, setTableVisible] = useState(false)

    const { hasPermission } = usePermissions();
    const [searchParams] = useSearchParams();
    const canEdit = hasPermission(PERMISSIONS.TIPO_MULTA_EDIT);
    const isViewOnly = searchParams.get('mode') === OPERATION_MODE.VIEW;
    const allowEditActions = canEdit && !isViewOnly;

    const filteredMultas = useMemo(() => {
        return multas.filter(x => (
            (filters.codigo === '' || x.codigo.toLowerCase().includes(filters.codigo.toLowerCase()))
            && (filters.nombre === '' || x.nombre.toLowerCase().includes(filters.nombre.toLowerCase()))
            && (filters.idTipoTributo === 0 || x.idTipoTributo === filters.idTipoTributo)
        ))
    })

    const [, _getRowLista ] = useLista({
        listas: ['TipoTributo'],
        onLoaded: (listas, isSuccess, error) => {
          if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: {
          key: 'TipoTributo',
          timeout: 0,
        },
    })
    const getRowLista = (lista, id) => _getRowLista(lista, id) ?? {}

    const onNoSuccess = response => response.json()
        .then((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
            setIsLoading(false)
        })
        .catch((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
            setIsLoading(false)
        })

    const onError = error => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message)
        setIsLoading(false)
    }

    const onSearch = (newFilters) => {
        setFilters(newFilters)
        setIsLoading(true)

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.TIPO_MULTA,
            null,
            null,
            res => {
                res.json()
                    .then(setMultas)
                    .catch(onError)
                    .finally(() => {
                        setIsLoading(false)
                        setTableVisible(true)
                    })
            },
            onNoSuccess,
            onError,
        )
    }

    const onModalCancel = () => {
        setEditState({})
    }

    const onModalConfirm = (item) => {
        setIsLoading(true)

        if (editState.mode === 'add') {
            ServerRequest(
                REQUEST_METHOD.POST,
                null,
                true,
                APIS.URLS.TIPO_MULTA,
                null,
                item,
                res => res.json()
                    .then(data => {
                        setIsLoading(false)
                        setMultas(prev => prev.concat([data]))
                        setEditState({})
                    })
                    .catch(onError),
                onNoSuccess,
                onError,
            )
        }
        else {
            ServerRequest(
                REQUEST_METHOD.PUT,
                null,
                true,
                APIS.URLS.TIPO_MULTA,
                `/${item.id}`,
                item,
                res => res.json()
                    .then(data => {
                        setIsLoading(false)
                        setMultas(prev => prev.map(x => x.id === editState.item.id ? data : x))
                        setEditState({})
                    })
                    .catch(onError),
                onNoSuccess,
                onError,
            )
        }
    }

    const avmr = {
        onAdd: () => {
            setEditState({
                mode: 'add',
                item: {
                    id: -new Date(),
                    codigo: '',
                    nombre: '',
                    orden: 0,
                    idTipoTributo: 0,
                    idUnidadValor: 0,
                    valorDesde: 0,
                    valorHasta: 0,
                }
            })
        },
        onModify: (item) => {
            setEditState({
                mode: 'edit',
                item,
            })
        },
        onView: (item) => {
            setEditState({
                mode: 'view',
                item,
            })
        },
        onRemove: ({ id }) => {
            const tipoMulta = multas.find(x => x.id === id)
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar el tipo de multa ${tipoMulta.codigo}?`,
                onConfirm: () => {
                    setIsLoading(true)
                    ServerRequest(
                        REQUEST_METHOD.DELETE,
                        null,
                        true,
                        APIS.URLS.TIPO_MULTA,
                        `/${id}`,
                        null,
                        res => {
                            setMultas(prev => prev.filter(x => x.id !== id))
                            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Registro borrado')
                            setIsLoading(false)
                        },
                        onNoSuccess,
                        onError,
                    )
                }
            })
        }
    }

    return (
        <div>
            <SectionHeading
                titles={[
                    { title: 'Entidades' },
                    { title: 'Administración de listas', url: '/entidades' },
                    { title: 'Tipos de multas' },
                ]}
                onAddClick={allowEditActions ? avmr.onAdd : null} tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <TipoMultaFilters {...{onSearch}} />

                {tableVisible && (
                    <TableCustom
                        className={'TableCustomBase m-top-15'}
                        data={filteredMultas}
                        messageEmpty="No se encontraron resultados"
                        columns={[
                            { Header: 'Código', accessor: 'codigo', width: '30%' },
                            { Header: 'Nombre', accessor: 'nombre', width: '30%' },
                            { Header: 'Tipo de tributo', accessor: 'idTipoTributo', Cell: ({ value }) => getRowLista('TipoTributo', value).nombre ?? '', width: '30%' },
                        ]}
                        avmr={avmr}
                        editCode={PERMISSIONS.TIPO_MULTA_EDIT}
                        disabled={!allowEditActions}
                    />
                )}

                {editState.mode && <TipoMultaModal {...editState} onConfirm={onModalConfirm} onCancel={onModalCancel} />}
            </section>
            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    <FooterButtonVolver filters={previousFilters} to="/entidades" />
                </div>
            </footer>
        </div>
    )
}

export default TipoMultasView
